"use client";

import { useRef } from "react";
import { useInView, type UseInViewOptions } from "motion/react";
import dynamic from "next/dynamic";
import type { VideoPlayerInnerProps } from "./player";

export type VideoPlayerProps = VideoPlayerInnerProps & {
  /** Load eagerly for above-the-fold players. Default false: the media-chrome
   *  chunk is code-split and only fetched once the wrapper nears the viewport. */
  priority?: boolean;
  /** In-view lead distance for the lazy gate. @default "200px 0px" */
  rootMargin?: UseInViewOptions["margin"];
  /** Class on the sizing wrapper. Must establish a positioning context and
   *  size — i.e. `relative` plus dimensions (e.g. `relative aspect-video`) —
   *  since the player's children are absolutely positioned; this also reserves
   *  the box so the lazy load doesn't shift layout. When nesting inside an
   *  existing `relative` sized parent (as the home page does), it can be
   *  omitted. */
  className?: string;
};

const VideoPlayerInner = dynamic(
  () => import("./player").then((m) => m.VideoPlayerInner),
  { ssr: false },
);

export function VideoPlayer({
  priority = false,
  rootMargin = "200px 0px",
  className,
  ...inner
}: VideoPlayerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: rootMargin });
  const show = priority || inView;

  return (
    <div ref={ref} className={className}>
      {show ? <VideoPlayerInner {...inner} /> : null}
    </div>
  );
}
